<!-- HealthStatus.vue - Navbar Health Button Component -->
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  firebaseStatus,
  apiHealthStatus,
  dbStatus,
  apiProtectedHealthStatus,
} from '@/services/api/health'
import type { AppHealthStatus, ClickOutsideBinding } from '@/types/all'

const apiHealth = ref('Checking API health...')
const apiAuthHealth = ref('Checking protected routes...')
const dbHealth = ref('Checking database health...')
const apiVersion = ref('')

const firebaseHealth = ref<AppHealthStatus | null>(null)
const showDetails = ref(false)

// Overall status computation
const overallStatus = computed(() => {
  // If any service is down, status is offline
  if (apiHealth.value !== 'ok' || apiAuthHealth.value !== 'ok' || dbHealth.value !== 'ok') {
    return 'offline'
  }

  // If firebase has issues, status is warning
  if (firebaseHealth.value && !firebaseHealth.value.ok) {
    return 'warning'
  }

  // If everything is ok, status is healthy
  if (
    apiHealth.value === 'ok' &&
    apiAuthHealth.value === 'ok' &&
    dbHealth.value === 'ok' &&
    firebaseHealth.value &&
    firebaseHealth.value.ok
  ) {
    return 'healthy'
  }

  // Default to warning if we're still checking
  return 'warning'
})

// Heartbeat monitor SVG computations
const pathData = computed(() => {
  switch (overallStatus.value) {
    case 'healthy':
      return 'M0,10 L5,10 L7,3 L9,17 L11,5 L13,15 L15,10 L20,10'
    case 'warning':
      return 'M0,10 L5,10 L7,3 L9,19 L11,1 L13,19 L15,10 L20,10'
    default: // offline
      return 'M0,10 L20,10'
  }
})

const strokeColor = computed(() => {
  switch (overallStatus.value) {
    case 'healthy':
      return '#22c55e' // primary-gr-500
    case 'warning':
      return '#eab308' // yellow-500
    default:
      return '#ef4444' // red-500
  }
})

const statusTitle = computed(() => {
  switch (overallStatus.value) {
    case 'healthy':
      return 'All Systems Operational'
    case 'warning':
      return 'Performance Issues Detected'
    default:
      return 'Service Disruption'
  }
})

const toggleDetails = () => {
  showDetails.value = !showDetails.value
}

// Close dropdown when clicking outside
const closeDetails = () => {
  if (showDetails.value) {
    showDetails.value = false
  }
}

// Custom click-outside directive
const vClickOutside = {
  mounted(el: HTMLElement, binding: ClickOutsideBinding) {
    el.clickOutsideEvent = function (event: Event) {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value(event, el)
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: HTMLElement) {
    if (el.clickOutsideEvent) {
      document.removeEventListener('click', el.clickOutsideEvent)
    }
  },
}

onMounted(async () => {
  try {
    const result = await apiHealthStatus()
    apiHealth.value = result.status || 'Error: No status returned'
  } catch (error) {
    apiHealth.value = 'API Unavailable'
    console.log(error)
  }
  try {
    const result = await apiProtectedHealthStatus()
    apiAuthHealth.value = result.status || 'Error: No status returned'
    apiVersion.value = result.version || ''
  } catch (error) {
    apiAuthHealth.value = 'Unauthorized access to protected routes'
    console.log(error)
  }
  try {
    const result = await dbStatus()
    dbHealth.value = result.status || 'Error: No status returned'
  } catch (error) {
    dbHealth.value = 'Database Unavailable'
    console.log(error)
  }

  try {
    const result = await firebaseStatus()
    // Store the object as is, without trying to stringify or parse
    firebaseHealth.value = result
  } catch (error) {
    console.error('Error checking Firebase status:', error)
    // Keep as null on error
    firebaseHealth.value = null
  }
})
</script>

<template>
  <div class="relative" v-click-outside="closeDetails">
    <!-- Health Status Button -->
    <button
      @click="toggleDetails"
      class="cursor-pointer p-2 text-gray-500 hover:bg-gray-100 rounded-full focus:outline-none relative flex items-center justify-center"
      :aria-label="statusTitle"
    >
      <!-- SVG Heartbeat -->
      <div class="health-monitor-container w-5 h-5 flex items-center justify-center">
        <svg class="health-monitor" viewBox="0 0 20 20" width="20" height="20">
          <!-- Heartbeat line -->
          <path :d="pathData" :stroke="strokeColor" stroke-width="1.5" fill="none" />

          <!-- X mark for offline -->
          <path
            v-if="overallStatus === 'offline'"
            d="M8,6 L12,14 M12,6 L8,14"
            :stroke="strokeColor"
            stroke-width="1.5"
          />
        </svg>
      </div>

      <!-- Status indicator dot -->
      <span
        :class="`absolute -top-1 -right-1 inline-flex items-center justify-center w-2 h-2 rounded-full ${
          overallStatus === 'healthy'
            ? 'bg-primary-gr-500'
            : overallStatus === 'warning'
              ? 'bg-yellow-500'
              : 'bg-red-500'
        }`"
      >
        <span
          :class="`animate-ping absolute inline-flex h-full w-full rounded-full ${
            overallStatus === 'healthy'
              ? 'bg-primary-gr-400'
              : overallStatus === 'warning'
                ? 'bg-yellow-400'
                : 'bg-red-400'
          } opacity-75`"
        ></span>
      </span>
    </button>

    <!-- Dropdown Panel with Status Details -->
    <div
      v-show="showDetails"
      class="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
    >
      <div class="p-3">
        <div class="flex items-center space-x-2 mb-2">
          <div
            :class="`w-3 h-3 rounded-full ${
              overallStatus === 'healthy'
                ? 'bg-primary-gr-500'
                : overallStatus === 'warning'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`"
          ></div>
          <h3 class="font-semibold text-gray-800 text-sm">{{ statusTitle }}</h3>
        </div>

        <div class="text-xs space-y-2">
          <div>
            <p class="flex items-center">
              <span class="font-medium text-gray-700">API Status ({{ apiVersion }})</span>
            </p>
            <p class="flex items-center">
              <span class="inline-block w-20">API Online:</span>
              <span :class="apiHealth === 'ok' ? 'text-primary-gr-600' : 'text-red-600'">
                {{ apiHealth === 'ok' ? 'Connected ✅' : 'Unavailable ❌' }}
              </span>
            </p>
            <p class="flex items-center">
              <span class="inline-block w-20">Auth Check:</span>
              <span :class="apiAuthHealth === 'ok' ? 'text-primary-gr-600' : 'text-red-600'">
                {{ apiAuthHealth === 'ok' ? 'Connected ✅' : 'Unauthorized ❌' }}
              </span>
            </p>
            <p class="flex items-center">
              <span class="inline-block w-20">DB Check:</span>
              <span :class="dbHealth === 'ok' ? 'text-primary-gr-600' : 'text-red-600'">
                {{ dbHealth === 'ok' ? 'Connected ✅' : 'Unavailable ❌' }}
              </span>
            </p>
          </div>

          <div v-if="firebaseHealth">
            <h4 class="font-medium text-gray-700">
              Firebase Status:
              <span :class="firebaseHealth.ok ? 'text-primary-gr-600' : 'text-red-600'">
                {{ firebaseHealth.ok ? 'Healthy ✅' : 'Issues ❌' }}
              </span>
            </h4>
            <p class="flex items-center">
              <span class="inline-block w-20">Client:</span>
              <span :class="firebaseHealth.appClient ? 'text-primary-gr-600' : 'text-red-600'">
                {{ firebaseHealth.appClient ? '✅' : '❌' }}
              </span>
            </p>
            <p class="flex items-center">
              <span class="inline-block w-20">Auth:</span>
              <span :class="firebaseHealth.appAuth ? 'text-primary-gr-600' : 'text-red-600'">
                {{ firebaseHealth.appAuth ? '✅' : '❌' }}
              </span>
            </p>
            <p class="flex items-center">
              <span class="inline-block w-20">User:</span>
              <span :class="firebaseHealth.user.ok ? 'text-primary-gr-600' : 'text-red-600'">
                {{ firebaseHealth.user.ok ? '✅' : '❌' }}
              </span>
            </p>
            <p class="flex items-center">
              <span class="inline-block w-20">Company:</span>
              <span :class="firebaseHealth.company.ok ? 'text-primary-gr-600' : 'text-red-600'">
                {{ firebaseHealth.company.ok ? '✅' : '❌' }}
              </span>
            </p>
          </div>

          <div v-else>
            <p>Firebase Status: Loading...</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.health-monitor {
  max-width: 100%;
  height: auto;
}
</style>
